import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { api } from './Api';

const Statistik = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [umsatzJahr, setUmsatzJahr] = useState([]);
  const [umsatzMonate, setUmsatzMonate] = useState([]);
  const [flaschenJahr, setFlaschenJahr] = useState([]);
  const [flaschenMonate, setFlaschenMonate] = useState([]);
  const [zusammenfassung, setZusammenfassung] = useState(null);

  useEffect(() => {
    fetchStatistikDaten();
  }, []);

  const fetchStatistikDaten = async () => {
    setLoading(true);
    setError(null);

    try {
      // Alle Statistik-Daten parallel abrufen mit api (axios) statt fetch
      const [
        umsatzJahrRes,
        umsatzMonateRes,
        flaschenJahrRes,
        flaschenMonateRes,
        zusammenfassungRes
      ] = await Promise.all([
        api.get('/auth/statistik/umsatz-jahr'),
        api.get('/auth/statistik/umsatz-monate'),
        api.get('/auth/statistik/flaschen-jahr'),
        api.get('/auth/statistik/flaschen-monate'),
        api.get('/auth/statistik/zusammenfassung')
      ]);

      // Axios gibt die Daten direkt in response.data zurück
      setUmsatzJahr(umsatzJahrRes.data);
      setUmsatzMonate(umsatzMonateRes.data);
      setFlaschenJahr(flaschenJahrRes.data);
      setFlaschenMonate(flaschenMonateRes.data);
      setZusammenfassung(zusammenfassungRes.data);
    } catch (err) {
      setError(err.message || 'Fehler beim Abrufen der Statistikdaten');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('de-DE').format(value);
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laden...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          Fehler beim Laden der Statistiken: {error}
        </Alert>
      </Container>
    );
  }

  // Diagramm-Konfigurationen
  const plotLayout = {
    font: { family: 'Arial, sans-serif' },
    showlegend: true,
    legend: { orientation: 'h', y: -0.15 },
    margin: { t: 40, r: 20, b: 60, l: 60 },
    hovermode: 'x unified'
  };

  const plotConfig = {
    displayModeBar: false,
    responsive: true
  };

  return (
    <Container fluid className="mt-4">
      <h2 className="mb-4">Statistiken</h2>

      {/* Zusammenfassung */}
      {zusammenfassung && (
        <Row className="mb-4">
          <Col md={3} className="mb-3">
            <Card className="h-100">
              <Card.Body>
                <Card.Title>Einkauf</Card.Title>
                <h4>{formatCurrency(zusammenfassung.einkauf_summe)}</h4>
                <p className="text-muted">{formatNumber(zusammenfassung.einkauf_anzahl)} Flaschen</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} className="mb-3">
            <Card className="h-100">
              <Card.Body>
                <Card.Title>Getrunken</Card.Title>
                <h4>{formatCurrency(zusammenfassung.getrunken_summe)}</h4>
                <p className="text-muted">{formatNumber(zusammenfassung.getrunken_anzahl)} Flaschen</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} className="mb-3">
            <Card className="h-100">
              <Card.Body>
                <Card.Title>Verschenkt</Card.Title>
                <h4>{formatCurrency(zusammenfassung.verschenkt_summe)}</h4>
                <p className="text-muted">{formatNumber(zusammenfassung.verschenkt_anzahl)} Flaschen</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} className="mb-3">
            <Card className="h-100">
              <Card.Body>
                <Card.Title>Lager</Card.Title>
                <h4>{formatCurrency(zusammenfassung.lager_summe)}</h4>
                <p className="text-muted">{formatNumber(zusammenfassung.lager_anzahl)} Flaschen</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Umsatz pro Jahr */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Card.Title>Umsatz pro Jahr</Card.Title>
              <Plot
                data={[
                  {
                    x: umsatzJahr.map(d => d.jahr),
                    y: umsatzJahr.map(d => d.einkauf),
                    type: 'bar',
                    name: 'Einkauf',
                    marker: { color: '#28a745' }
                  },
                  {
                    x: umsatzJahr.map(d => d.jahr),
                    y: umsatzJahr.map(d => d.getrunken),
                    type: 'bar',
                    name: 'Getrunken',
                    marker: { color: '#17a2b8' }
                  },
                  {
                    x: umsatzJahr.map(d => d.jahr),
                    y: umsatzJahr.map(d => d.verschenkt),
                    type: 'bar',
                    name: 'Verschenkt',
                    marker: { color: '#ffc107' }
                  }
                ]}
                layout={{
                  ...plotLayout,
                  title: 'Umsatz pro Jahr (€)',
                  xaxis: { 
                    title: 'Jahr',
                    tickmode: 'linear',
                    tick0: umsatzJahr.length > 0 ? Math.min(...umsatzJahr.map(d => d.jahr)) : 2020,
                    dtick: 1
                  },
                  yaxis: { title: 'Betrag (€)' },
                  barmode: 'group'
                }}
                config={plotConfig}
                style={{ width: '100%', height: '400px' }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Umsatz letzte 12 Monate */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Card.Title>Umsatz letzte 12 Monate</Card.Title>
              <Plot
                data={[
                  {
                    x: umsatzMonate.map(d => `${d.monat} ${d.jahr}`),
                    y: umsatzMonate.map(d => d.einkauf),
                    type: 'scatter',
                    mode: 'lines+markers',
                    name: 'Einkauf',
                    line: { color: '#28a745' }
                  },
                  {
                    x: umsatzMonate.map(d => `${d.monat} ${d.jahr}`),
                    y: umsatzMonate.map(d => d.getrunken),
                    type: 'scatter',
                    mode: 'lines+markers',
                    name: 'Getrunken',
                    line: { color: '#17a2b8' }
                  },
                  {
                    x: umsatzMonate.map(d => `${d.monat} ${d.jahr}`),
                    y: umsatzMonate.map(d => d.verschenkt),
                    type: 'scatter',
                    mode: 'lines+markers',
                    name: 'Verschenkt',
                    line: { color: '#ffc107' }
                  }
                ]}
                layout={{
                  ...plotLayout,
                  title: 'Umsatz letzte 12 Monate (€)',
                  xaxis: { title: 'Monat' },
                  yaxis: { title: 'Betrag (€)' }
                }}
                config={plotConfig}
                style={{ width: '100%', height: '400px' }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Flaschen pro Jahr */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Card.Title>Flaschen pro Jahr</Card.Title>
              <Plot
                data={[
                  {
                    x: flaschenJahr.map(d => d.jahr),
                    y: flaschenJahr.map(d => d.einkauf),
                    type: 'bar',
                    name: 'Einkauf',
                    marker: { color: '#28a745' }
                  },
                  {
                    x: flaschenJahr.map(d => d.jahr),
                    y: flaschenJahr.map(d => d.getrunken),
                    type: 'bar',
                    name: 'Getrunken',
                    marker: { color: '#17a2b8' }
                  },
                  {
                    x: flaschenJahr.map(d => d.jahr),
                    y: flaschenJahr.map(d => d.verschenkt),
                    type: 'bar',
                    name: 'Verschenkt',
                    marker: { color: '#ffc107' }
                  }
                ]}
                layout={{
                  ...plotLayout,
                  title: 'Anzahl Flaschen pro Jahr',
                  xaxis: { 
                    title: 'Jahr',
                    tickmode: 'linear',
                    tick0: flaschenJahr.length > 0 ? Math.min(...flaschenJahr.map(d => d.jahr)) : 2020,
                    dtick: 1
                  },
                  yaxis: { title: 'Anzahl Flaschen' },
                  barmode: 'group'
                }}
                config={plotConfig}
                style={{ width: '100%', height: '400px' }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Flaschen letzte 12 Monate */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Card.Title>Flaschen letzte 12 Monate</Card.Title>
              <Plot
                data={[
                  {
                    x: flaschenMonate.map(d => `${d.monat} ${d.jahr}`),
                    y: flaschenMonate.map(d => d.einkauf),
                    type: 'scatter',
                    mode: 'lines+markers',
                    name: 'Einkauf',
                    line: { color: '#28a745' }
                  },
                  {
                    x: flaschenMonate.map(d => `${d.monat} ${d.jahr}`),
                    y: flaschenMonate.map(d => d.getrunken),
                    type: 'scatter',
                    mode: 'lines+markers',
                    name: 'Getrunken',
                    line: { color: '#17a2b8' }
                  },
                  {
                    x: flaschenMonate.map(d => `${d.monat} ${d.jahr}`),
                    y: flaschenMonate.map(d => d.verschenkt),
                    type: 'scatter',
                    mode: 'lines+markers',
                    name: 'Verschenkt',
                    line: { color: '#ffc107' }
                  }
                ]}
                layout={{
                  ...plotLayout,
                  title: 'Anzahl Flaschen letzte 12 Monate',
                  xaxis: { title: 'Monat' },
                  yaxis: { title: 'Anzahl Flaschen' }
                }}
                config={plotConfig}
                style={{ width: '100%', height: '400px' }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Statistik;
